import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchApi } from "@/services/apiService";

type Props = {
  leadId: number;
};

const statusBadgeColors: Record<string, string> = {
  Draft: "bg-gray-100 text-gray-700",
  Sent: "bg-blue-100 text-blue-700",
  Accepted: "bg-green-100 text-green-700",
  Declined: "bg-red-100 text-red-700",
  Expired: "bg-amber-100 text-amber-700",
};

export function LeadQuotationsCard({ leadId }: Props) {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    setLoading(true);
    fetchApi<any[]>(`/quotations?leadId=${leadId}`, undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        setQuotations(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId]);

  const formatCurrency = (val: number) => {
    if (!val) return "₹0";
    if (val >= 100000) return "₹" + (val / 100000).toFixed(1) + "L";
    return "₹" + Number(val).toLocaleString("en-IN");
  };

  const formatDate = (d: string) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Quotations</h3>
          {quotations.length > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-[10px] font-bold text-gray-600">
              {quotations.length}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate(`/quotations/new?leadId=${leadId}`)}
          className="text-xs font-medium text-indigo-600 hover:text-indigo-700 whitespace-nowrap cursor-pointer"
        >
          + Create Quotation
        </button>
      </div>

      {loading ? (
        <div className="py-4 text-center text-xs text-gray-400">
          <i className="ri-loader-4-line animate-spin mr-1 text-sm" /> Loading quotations...
        </div>
      ) : quotations.length === 0 ? (
        <p className="text-xs text-gray-400 py-3 text-center">No quotations yet for this lead</p>
      ) : (
        <div className="space-y-2">
          {quotations.slice(0, 3).map((q) => {
            const total = Number(q.total_amount || q.total || 0);
            const quoteNum = q.quote_number || q.quoteNo || `QT-${q.id}`;
            const status = q.status || "Draft";
            const date = q.created_at || q.date;

            return (
              <button
                key={q.id}
                onClick={() => navigate(`/quotations/${q.id}`)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-semibold text-indigo-600 whitespace-nowrap">{quoteNum}</span>
                  <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{formatCurrency(total)}</span>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadgeColors[status] || "bg-gray-100 text-gray-700"}`}>
                    {status}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-gray-400">{formatDate(date)}</span>
                  <i className="ri-arrow-right-s-line text-gray-400 text-sm" />
                </div>
              </button>
            );
          })}
          {quotations.length > 3 && (
            <button
              onClick={() => navigate("/quotations")}
              className="w-full text-center text-xs text-indigo-600 hover:text-indigo-700 font-medium py-1 cursor-pointer"
            >
              View all {quotations.length} quotations →
            </button>
          )}
        </div>
      )}
    </div>
  );
}